import { useEffect, useState } from "react";
import { Baby, Loader2, Plus, Trash2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";

/** Daycare settings: age-group ratios, parent PIN sign-in, end-of-day report time. */
export function DaycareSettingsCard() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/daycare/settings").then((r) => setForm(r.data)).catch(() => {}); }, []);
  if (!form) return null;

  const setGroup = (i, patch) => setForm((f) => ({ ...f, age_groups: f.age_groups.map((g, j) => (j === i ? { ...g, ...patch } : g)) }));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/daycare/settings", { ...form, age_groups: form.age_groups.map((g) => ({ ...g, key: g.key || g.name.toLowerCase().replace(/\W+/g, "-"), max_months: Number(g.max_months) || 12, ratio: Number(g.ratio) || 1 })) });
      setForm(data); toast.success("Daycare settings saved");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    try { const { data } = await api.post("/daycare/report/send"); toast.success(`Daily report sent (${data.emails_sent} email${data.emails_sent === 1 ? "" : "s"})`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="daycare-settings-card">
      <div className="flex items-center gap-2 mb-1"><Baby className="h-4 w-4 text-primary" /><h2 className="font-display text-lg font-semibold text-slate-900">Daycare / Pre-K</h2></div>
      <p className="text-xs text-slate-500 mb-5">Ratios per age group, parent kiosk PIN sign-in and the end-of-day “{`{child}`}'s day” email.</p>

      <div className="space-y-3 mb-5">
        <div className="grid grid-cols-[1fr_110px_90px_32px] gap-2 text-[11px] uppercase tracking-widest text-slate-500"><span>Age group</span><span>Up to (months)</span><span>Ratio 1:</span><span /></div>
        {form.age_groups.map((g, i) => (
          <div key={i} className="grid grid-cols-[1fr_110px_90px_32px] gap-2 items-center" data-testid={`age-group-row-${i}`}>
            <input value={g.name} onChange={(e) => setGroup(i, { name: e.target.value })} className={inputCls} />
            <input type="number" min={1} value={g.max_months} onChange={(e) => setGroup(i, { max_months: e.target.value })} className={inputCls} />
            <input type="number" min={1} value={g.ratio} onChange={(e) => setGroup(i, { ratio: e.target.value })} className={inputCls} data-testid={`age-group-ratio-${i}`} />
            <button onClick={() => setForm((f) => ({ ...f, age_groups: f.age_groups.filter((_, j) => j !== i) }))} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={() => setForm((f) => ({ ...f, age_groups: [...f.age_groups, { key: "", name: "", max_months: 12, ratio: 6 }] }))} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Plus className="h-3.5 w-3.5" /> Add age group</button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <label className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
          <input type="checkbox" checked={form.parent_pin_signin} onChange={(e) => setForm((f) => ({ ...f, parent_pin_signin: e.target.checked }))} data-testid="daycare-pin-toggle" />
          <span><span className="block font-medium text-slate-900">Parent PIN sign-in at kiosk</span><span className="block text-xs text-slate-500">Parents sign children in/out with their family PIN</span></span>
        </label>
        <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={form.report_enabled} onChange={(e) => setForm((f) => ({ ...f, report_enabled: e.target.checked }))} data-testid="daycare-report-toggle" />
            <span className="font-medium text-slate-900">Email daily report at</span>
            <input type="time" value={form.report_time} onChange={(e) => setForm((f) => ({ ...f, report_time: e.target.value }))} className="h-8 rounded-lg border border-slate-200 px-2 text-sm" data-testid="daycare-report-time" />
          </label>
          <button onClick={sendNow} className="mt-1.5 text-xs text-primary hover:underline" data-testid="daycare-report-send-now">Send today's reports now</button>
        </div>
      </div>

      <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:shadow disabled:opacity-60" data-testid="daycare-settings-save">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save daycare settings
      </button>
    </section>
  );
}
